<template>
  <UnsavedChangesModal ref="unsavedChangesModal" />

  <div v-if="currentSupplier">

    <div class="container-fluid">
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }" :disabled="isSaving">
        <ul class="nav nav-tabs mt-2 mb-2" id="myTab" role="tablist">
          <li class="nav-item" role="presentation">
            <RouterLink :to="TranslationsService.i18nRoute({ name: 'suppliers' })" class="nav-link text-primary fw-bold" active-class="">
              <font-awesome-icon :icon="['fas', 'pen']" v-if="editMode" />
              <font-awesome-icon :icon="['fas', 'person-dolly']" v-else />
              {{ supplierName }}
            </RouterLink>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link active" id="head-tab" data-bs-toggle="tab" data-bs-target="#head-tab-pane" type="button" role="tab" aria-controls="head-tab-pane" aria-selected="true">{{ $t('field.generic.general') }}</button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link" id="detail-tab" data-bs-toggle="tab" data-bs-target="#detail-tab-pane" type="button" role="tab" aria-controls="detail-tab-pane" aria-selected="false">{{ $t('field.supplier.assignedItems') }} <span class="badge text-bg-secondary" :class="{ 'text-bg-info': selectedItems.length === 0 }">{{ selectedItems.length }}</span></button>
          </li>
          <li class="nav-item" role="presentation">
            <div v-if="editMode" class="ms-3 input-group">
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !requiredFieldsComplete">
                <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
                <font-awesome-icon v-else :icon="['fas', 'floppy-disk']" />
                {{ $t('action.save') }}
              </button>
              <button type="button" class="btn btn-outline-secondary" @click="disableEditMode()" :disabled="isSaving">
                <font-awesome-icon :icon="['fas', 'xmark']" />
                {{ $t('action.cancel') }}
              </button>
              <template v-if="detailTabActive">
                <span class="input-group-text ms-2">
                  <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                </span>
                <input id="itemFilter" type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')"/>
                <button v-if="itemFilter" type="button" class="btn btn-outline-secondary" @click="clearFilter" :title="$t('action.clear')">
                  <font-awesome-icon :icon="['fas', 'filter-slash']" />
                </button>
              </template>
            </div>
            <div v-else class="ms-3 btn-group d-none d-md-inline">
              <button type="button" class="btn btn-outline-primary" :disabled="isSubmitting" @click="enableEditMode()">
                <font-awesome-icon :icon="['fas', 'pen']" />
                {{ $t('action.edit') }}
              </button>
            </div>
            <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
          </li>
        </ul>
        <div class="tab-content" id="myTabContent">
          <div class="tab-pane fade show active container align-items-center" id="head-tab-pane" role="tabpanel" aria-labelledby="head-tab" tabindex="0">
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="code" class="form-label" :class="{ 'required': editMode }" :title="$t('field.supplier.supplierCodeTitle')">{{ $t('field.supplier.supplierCode') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="code" name="code" type="text" class="form-control" :class="{ 'is-invalid': errors.code }" maxlength="20" v-model="supplierCode" readonly v-if="editMode" />
                <span v-else>{{ supplierCode }}</span>
                <div class="invalid-feedback" v-if="!!errors.code">{{ errors.code }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="name" class="form-label" :class="{ 'required': editMode }">{{ $t('field.generic.name') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="name" name="name" type="text" class="form-control" :class="{ 'is-invalid': errors.name }" maxlength="100" v-model="supplierName" v-if="editMode" />
                <span v-else>{{ supplierName }}</span>
                <div class="invalid-feedback" v-if="!!errors.name">{{ errors.name }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="email" class="form-label" :class="{ 'required': editMode }">{{ $t('field.generic.email') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="email" name="email" type="text" class="form-control" :class="{ 'is-invalid': errors.email }" maxlength="256" v-model="supplierEmail" v-if="editMode" />
                <span v-else>{{ supplierEmail }}</span>
                <div class="invalid-feedback" v-if="!!errors.email">{{ errors.email }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="cultureName" class="form-label" :title="$t('field.supplier.cultureNameTitle')">{{ $t('field.supplier.cultureName') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="cultureName" name="cultureName" as="select" class="form-select" style="max-width: 16rem;" v-model="supplierCultureName" v-if="editMode">
                  <option v-for="culture in selectableCultures" :key="culture.code" :value="culture.code">{{ $t(culture.nameKey) }}</option>
                </Field>
                <span v-else>{{ $t(selectableCultures.find(c => c.code === supplierCultureName)?.nameKey ?? `language.${defaultMailCulture}`) }}</span>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="customerCode" class="form-label" :class="{ 'required': editMode }" :title="$t('field.supplier.customerCodeTitle')">{{ $t('field.supplier.customerCode') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="customerCode" name="customerCode" type="text" class="form-control" :class="{ 'is-invalid': errors.customerCode }" maxlength="20" v-model="supplierCustomerCode" v-if="editMode" />
                <span v-else>{{ supplierCustomerCode }}</span>
                <div class="invalid-feedback" v-if="!!errors.customerCode">{{ errors.customerCode }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="companyName" class="form-label">{{ $t('field.generic.company') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="companyName" name="companyName" type="text" class="form-control" :class="{ 'is-invalid': errors.companyName }" maxlength="100" v-model="supplierCompanyName" v-if="editMode" />
                <span v-else>{{ supplierCompanyName }}</span>                
                <div class="invalid-feedback" v-if="!!errors.companyName">{{ errors.companyName }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="address" class="form-label">{{ $t('field.generic.address') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="address" name="address" type="text" as="textarea" rows="4" class="form-control" :class="{ 'is-invalid': errors.address }" maxlength="400" v-model="supplierAddress" v-if="editMode" />
                <span v-else v-html="addressFormatted"></span>
                <div class="invalid-feedback" v-if="!!errors.address">{{ errors.address }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="contactName" class="form-label">{{ $t('field.generic.contactName') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="contactName" name="contactName" type="text" class="form-control" :class="{ 'is-invalid': errors.contactName }" maxlength="100" v-model="supplierContactName" v-if="editMode" />
                <span v-else>{{ supplierContactName }}</span>
                <div class="invalid-feedback" v-if="!!errors.contactName">{{ errors.contactName }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="phone" class="form-label">{{ $t('field.generic.phone') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <Field id="phone" name="phone" type="text" class="form-control" :class="{ 'is-invalid': errors.phone }" maxlength="20" v-model="supplierPhone" v-if="editMode" />
                <span v-else>{{ supplierPhone }}</span>
                <div class="invalid-feedback" v-if="!!errors.phone">{{ errors.phone }}</div>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="autoOrderEnabled" class="form-label" :title="$t('field.supplier.autoOrderEnabledTitle')">{{ $t('field.supplier.autoOrderEnabled') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <div class="form-check" v-if="editMode">
                  <Field id="autoOrderEnabled" name="autoOrderEnabled" type="checkbox" class="form-check-input" :value="true" :unchecked-value="false" v-model="supplierAutoOrderEnabled" />
                </div>
                <span v-else>
                  <font-awesome-icon :icon="['fas', supplierAutoOrderEnabled ? 'check' : 'xmark']" :class="supplierAutoOrderEnabled ? 'text-success' : 'text-muted'" />
                </span>
              </dd>
            </dl>
            <dl class="row mb-0">
              <dt class="col-sm-4 col-lg-2">
                <label for="autoConfirmOrder" class="form-label" :title="$t('field.supplier.autoConfirmOrderTitle')">{{ $t('field.supplier.autoConfirmOrder') }}</label>
              </dt>
              <dd class="col-sm-8 col-lg-10">
                <div class="form-check" v-if="editMode">
                  <Field id="autoConfirmOrder" name="autoConfirmOrder" type="checkbox" class="form-check-input" :value="true" :unchecked-value="false" v-model="supplierAutoConfirmOrder" />
                </div>
                <span v-else>
                  <font-awesome-icon :icon="['fas', supplierAutoConfirmOrder ? 'check' : 'xmark']" :class="supplierAutoConfirmOrder ? 'text-success' : 'text-muted'" />
                </span>
              </dd>
            </dl>
          </div>
          <div class="tab-pane fade table-fix-head" id="detail-tab-pane" :style="fixDetailTabPaneCalculatedStyle" role="tabpanel" aria-labelledby="detail-tab" tabindex="0">
            <table v-if="editMode" class="table table-borderless table-sm table-hover table-fit align-middle">
              <thead>
                <tr>
                  <th class="mel-bar"></th>
                  <th>{{ $t('field.generic.item') }}</th>
                  <th>{{ $t('field.order.orderedQuantity') }}</th>
                  <th>{{ $t('field.masterdata.orderUnitCode') }}</th>
                  <th>{{ $t('field.supplier.itemCode') }}</th>
                  <th>{{ $t('field.supplier.assignedSupplier') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entry in filteredItemsList" :key="entry.item.id" :class="{ 'is-filled': entry.IsSelected }">
                  <td class="mel-bar" :class="{ 'mel-bar-primary': entry.IsSelected }"></td>
                  <td><ItemField :item="entry.item" /></td>
                  <td><input type="number" class="form-control" v-model="entry.proposedOrderQuantity" min="1" max="1000000" size="4" style="min-width: 32px;" /></td>
                  <td>
                    <select class="form-select" v-model="entry.orderUnitCode" :disabled="entry.itemUnitCodes.size === 1 && entry.orderUnitCode === entry.itemUnitCodes.keys().next().value">
                      <option v-for="[unitCode, unitDisplayName] in entry.itemUnitCodes" :key="unitCode" :value="unitCode">{{ unitDisplayName }}</option>
                    </select>
                  </td>
                  <td>
                    <input type="text" class="form-control" v-model="entry.code" maxlength="20" :id="'codeInput' + entry.item.id" />
                  </td>
                  <td>
                    <!-- new supplier -->
                    <span v-if="!entry.supplier && entry.IsSelected">
                      <span class="text-primary">{{ supplierName }}</span>
                      <font-awesome-icon :icon="['fas', 'pen']" class="ms-2" />
                    </span>
                    <!-- no supplier -->
                    <span v-else-if="!entry.supplier && !entry.IsSelected">
                      <span class="text-warning fw-bold"><font-awesome-icon :icon="['fas', 'triangle-exclamation']" /> {{ $t('error.noItemSupplierAssigned') }}</span>
                    </span>
                    <!-- current supplier -->
                    <span v-else-if="entry.supplier && entry.IsSelected && entry.IsSelectedSupplier">
                      <span class="text-primary fw-bold">{{ entry.supplier.name }}</span>
                    </span>
                    <!-- remove supplier -->
                    <span v-else-if="entry.supplier && !entry.IsSelected && entry.IsSelectedSupplier">
                      <span class="text-primary text-decoration-line-through">{{ entry.supplier.name }}</span>
                    </span>
                    <!-- other supplier -->
                    <span v-else-if="entry.supplier && !entry.IsSelected && !entry.IsSelectedSupplier">
                      <span class="text-melodi">{{ entry.supplier.name }}</span>
                    </span>
                    <!-- change supplier -->
                    <span v-else-if="entry.supplier && entry.IsSelected && !entry.IsSelectedSupplier">
                      <span class="text-melodi text-sm text-decoration-line-through">{{ entry.supplier.name }}</span><br>
                      <span class="text-primary">{{ supplierName }}</span>
                      <font-awesome-icon :icon="['fas', 'pen']" class="ms-2" />
                    </span>
                  </td>
                  <td>
                    <button type="button" class="btn btn-primary" v-if="!entry.code" @click="assignCurrentSupplier(entry)">
                      <font-awesome-icon :icon="['fas', 'person-dolly']" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <template v-else>
              <template v-if="selectedItems.length === 0">
                <div class="alert alert-info" role="alert">
                  <h4 class="alert-heading">
                    <font-awesome-layers class="fa-1x me-2">
                      <font-awesome-icon :icon="['fas', 'circle-info']" />
                    </font-awesome-layers>
                    {{ $t('error.supplierHasNoAssignedItems', { supplierName: currentSupplier.name }) }}
                  </h4>
                  {{ $t('help.assignItemsToSupplierWithoutItems') }}
                </div>
                <button type="button" class="btn btn-primary btn-lg w-100" :disabled="isSubmitting" @click="enableEditMode()">
                  <font-awesome-icon :icon="['fas', 'pen']" />
                  {{ $t('action.edit') }}
                </button>
              </template>
              <SupplierItemList v-else :supplier-id="currentSupplier.id" />
            </template>
          </div>
        </div>
      </Form>
    </div>

  </div>
  <div v-else>
    <span>{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { Id, toast, type ToastOptions } from 'vue3-toastify';
import * as Yup from 'yup';
import { Form, Field, GenericObject } from 'vee-validate';
import { useSessionStore, useItemsStore, useSuppliersStore, useSupplierItemsStore, useReplacementItemsStore, useTradeItemsStore } from '@/stores';
import { useI18n } from 'vue-i18n';
import { ItemField } from '@/components/fields';
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue';
import { useUnsavedChangesGuard } from '@/services/unsavedChangesGuard';
import UpsertSupplierRequest from '@/models/requests/UpsertSupplierRequest';
import { SyncSupplierItemRequest } from '@/models/requests/SyncSupplierItemsRequest';
import SupplierItemList from '@/components/masterdata/SupplierItemList.vue';
import Item from '@/models/frontend/Item';
import Supplier from '@/models/frontend/Supplier';
import SupplierWithItems from '@/models/frontend/SupplierWithItems';
import TranslationsService from '@/services/translations.ts';

const props = defineProps({
  supplierId: { type: Number, required: true }
});

const currentSupplier = ref(undefined as Supplier | undefined);
const supplierCode = ref('');
const supplierName = ref('');
const supplierEmail = ref('');
const supplierCustomerCode = ref('');
const supplierCompanyName = ref(undefined as string | undefined);
const supplierAddress = ref(undefined as string | undefined);
const supplierContactName = ref(undefined as string | undefined);
const supplierPhone = ref(undefined as string | undefined);
const defaultMailCulture = 'de';
const supplierCultureName = ref(defaultMailCulture as string);
const supplierAutoConfirmOrder = ref(true);
const supplierAutoOrderEnabled = ref(true);

const selectableCultures = computed(() => {
  const cultures = [
    { code: 'en', nameKey: 'language.en' },
    { code: 'de', nameKey: 'language.de' },
    { code: 'fr', nameKey: 'language.fr' }
  ];
  return cultures.sort((a, b) => i18n.t(a.nameKey).localeCompare(i18n.t(b.nameKey), i18n.locale.value));
});
const editMode = ref(false);
const itemFilter = ref('');
const activeTab = ref('head-tab-pane'); // Track the active tab
const detailPaneOffsetTop = ref(0);
const initialFormSnapshot = ref('');
const initialItemsSnapshot = ref('');
const unsavedChangesModal = ref<InstanceType<typeof UnsavedChangesModal> | null>(null);

class SelectableItem {
  constructor(
    public item: Item,
    public itemUnitCodes: Map<string, string>,
    public code: string,
    public proposedOrderQuantity: number | undefined,
    public orderUnitCode: string,
    public name: string | undefined = undefined,
    public description: string | undefined = undefined,
    public supplier: Supplier | undefined = undefined
  ) { }

  get IsSelected(): boolean {
    return this.code !== '' && this.orderUnitCode !== '';
  }
  get IsSelectedSupplier(): boolean {
    return this.supplier !== undefined && this.supplier.id === currentSupplier.value?.id;
  }
}

const sessionStore = useSessionStore();
const itemsStore = useItemsStore();
const tradeItemsStore = useTradeItemsStore();
const suppliersStore = useSuppliersStore();
const supplierItemsStore = useSupplierItemsStore();
const replacementItemsStore = useReplacementItemsStore();

const { selectedTenantId } = storeToRefs(sessionStore);
const { data: items } = storeToRefs(itemsStore);

const i18n = useI18n();

const isSaving = ref(false);
const toastId = ref('' as Id);
const itemsList = ref([] as SelectableItem[]);

const selectedItems = computed(() => itemsList.value.filter((item) => item.IsSelected));

const addressFormatted = computed(() => {
  return supplierAddress.value?.replace(/\n/g, '<br>');
});

const schema = Yup.object().shape({
  code: Yup.string().required(i18n.t('validation.codeRequired')).max(20, i18n.t('validation.maxLength', { max: 20 })),
  name: Yup.string().required(i18n.t('validation.nameRequired')).max(100, i18n.t('validation.maxLength', { max: 100 })),
  email: Yup.string().email(i18n.t('validation.emailRequired')).required(i18n.t('validation.emailRequired')).max(256, i18n.t('validation.maxLength', { max: 256 })),
  customerCode: Yup.string().required(i18n.t('validation.required')).max(20, i18n.t('validation.maxLength', { max: 20 })),
  companyName: Yup.string().notRequired().max(100, i18n.t('validation.maxLength', { max: 100 })),
  address: Yup.string().notRequired().max(400, i18n.t('validation.maxLength', { max: 400 })),
  contactName: Yup.string().notRequired().max(100, i18n.t('validation.maxLength', { max: 100 })),
  phone: Yup.string().notRequired().max(20, i18n.t('validation.maxLength', { max: 20 })),
  cultureName: Yup.string().notRequired().max(10, i18n.t('validation.maxLength', { max: 10 })),
  autoConfirmOrder: Yup.boolean().notRequired(),
  autoOrderEnabled: Yup.boolean().notRequired(),
});

const requiredFieldsComplete = computed(() => supplierCode.value !== '' && supplierName.value !== '' && supplierEmail.value !== '' && supplierCustomerCode.value !== '');

const filteredItemsList = computed(() => {
  if (!itemFilter.value.trim()) {
    return itemsList.value;
  }
  
  const filterText = itemFilter.value.toLowerCase().trim();
  return itemsList.value.filter(entry => {
    const item = entry.item;
    return (
      item.name.toLowerCase().includes(filterText) ||
      item.code.toLowerCase().includes(filterText) ||
      (item.description && item.description.toLowerCase().includes(filterText))
    );
  });
});

const clearFilter = () => {
  itemFilter.value = '';
};

// Computed property to check if the detail tab is active
const detailTabActive = computed(() => {
  return activeTab.value === 'detail-tab-pane';
});

const computeFormSnapshot = () => JSON.stringify({
  code: supplierCode.value,
  name: supplierName.value,
  email: supplierEmail.value,
  customerCode: supplierCustomerCode.value,
  companyName: supplierCompanyName.value ?? '',
  address: supplierAddress.value ?? '',
  contactName: supplierContactName.value ?? '',
  phone: supplierPhone.value ?? '',
  cultureName: supplierCultureName.value,
  autoConfirmOrder: supplierAutoConfirmOrder.value,
  autoOrderEnabled: supplierAutoOrderEnabled.value,
});

const computeItemsSnapshot = () => JSON.stringify(itemsList.value.map(i => ({
  id: i.item.id,
  code: i.code,
  proposedOrderQuantity: i.proposedOrderQuantity,
  orderUnitCode: i.orderUnitCode,
})));

const hasUnsavedChanges = computed(() => {
  if (!editMode.value) return false;
  return computeFormSnapshot() !== initialFormSnapshot.value
    || computeItemsSnapshot() !== initialItemsSnapshot.value;
});

const enableEditMode = () => {
  initialFormSnapshot.value = computeFormSnapshot();
  initialItemsSnapshot.value = computeItemsSnapshot();
  editMode.value = true;
};

const disableEditMode = async () => {
  await initData();
  editMode.value = false;
};

const onApiFulfilled = async (supplier: SupplierWithItems) => {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('statement.supplierSaved', { supplierCode: supplier.code }),
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    autoClose: 10000,
    closeOnClick: true
  } as ToastOptions);
  editMode.value = false;
  await initData();
};

const onApiRejected = (error: Error) => {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('error.supplierSaveError', { error: error.message }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false
  } as ToastOptions);
};

const onSubmit = async (values: GenericObject, { setErrors }: GenericObject) => {
  const { code, name, email, customerCode, companyName, address, contactName, phone, cultureName, autoConfirmOrder, autoOrderEnabled } = values;
  const selectedItems = itemsList.value.filter((item) => item.IsSelected);

  if (currentSupplier.value === undefined) {
    return setErrors({ apiError: 'Supplier not found' });
  }

  // check if selected items have unique codes and show which ones are duplicates
  const codes = selectedItems.map((entry) => entry.code);
  const duplicates = codes.filter((code, index) => codes.indexOf(code) !== index);
  if (duplicates.length > 0) {
    toast.warn(i18n.t('validation.duplicateCodes', { duplicates: duplicates.join(', ') }), { autoClose: 10000, theme: 'colored' });
    return;
  }

  try {
    isSaving.value = true;
    toastId.value = toast.loading(i18n.t('status.savingEllipsis'), {
      autoClose: false,
      theme: 'colored'
    } as ToastOptions);
    const selectedUpsertItems = selectedItems.map((entry) => new SyncSupplierItemRequest(entry.item.id, entry.code, entry.orderUnitCode, entry.name, entry.description, entry.proposedOrderQuantity));
    const newOrUpdatedSupplier = new UpsertSupplierRequest(selectedTenantId.value, code, name, email, customerCode, companyName, address, contactName, phone, cultureName, autoConfirmOrder ?? true, autoOrderEnabled ?? true, selectedUpsertItems);
    await suppliersStore.upsert(newOrUpdatedSupplier, onApiFulfilled, onApiRejected);
  } catch (error) {
    return setErrors({ apiError: error });
  }
};

// Event handler functions for tab switching
const handleHeadTabShow = () => {
  activeTab.value = 'head-tab-pane';
};

const handleDetailTabShow = () => {
  activeTab.value = 'detail-tab-pane';
  detailPaneOffsetTop.value = document.querySelector('#detail-tab-pane')?.getBoundingClientRect().top ?? 0;
};

useUnsavedChangesGuard(() => hasUnsavedChanges.value, unsavedChangesModal);

onMounted(async () => {
  await initData();

  // Set up Bootstrap tab event listeners
  const headTab = document.getElementById('head-tab');
  const detailTab = document.getElementById('detail-tab');

  if (headTab) {
    headTab.addEventListener('shown.bs.tab', handleHeadTabShow);
  }

  if (detailTab) {
    detailTab.addEventListener('shown.bs.tab', handleDetailTabShow);
  }
});

onUnmounted(() => {
  // Clean up event listeners
  const headTab = document.getElementById('head-tab');
  const detailTab = document.getElementById('detail-tab');

  if (headTab) {
    headTab.removeEventListener('shown.bs.tab', handleHeadTabShow);
  }

  if (detailTab) {
    detailTab.removeEventListener('shown.bs.tab', handleDetailTabShow);
  }
});

const fixDetailTabPaneCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${detailPaneOffsetTop.value}px)`,
  };
});

const getUnitCodeDisplayName = (baseItem: Item, itemId: number, unitCode: string): string => {
  const tradeItem = tradeItemsStore.getByItemIdAndUnitCode(itemId, unitCode);
  if (tradeItem === undefined || baseItem.baseUnitCode === tradeItem.unitCode) {
    return unitCode;
  }
  else {
    return `${unitCode} à ${tradeItem.factor} ${baseItem.baseUnitCode}`;
  }
};

const initData = async () => {
  const supplier = currentSupplier.value = suppliersStore.getById(props.supplierId);
  if (supplier === undefined) return;

  supplierCode.value = supplier.code;
  supplierName.value = supplier.name;
  supplierEmail.value = supplier.email;
  supplierCustomerCode.value = supplier.customerCode;
  supplierCompanyName.value = supplier.info.companyName;
  supplierAddress.value = supplier.info.address;
  supplierContactName.value = supplier.info.contactName;
  supplierPhone.value = supplier.info.phone;
  supplierCultureName.value = supplier.info.cultureName ?? defaultMailCulture;
  supplierAutoConfirmOrder.value = supplier.info.autoConfirmOrder ?? true;
  supplierAutoOrderEnabled.value = supplier.info.autoOrderEnabled ?? true;

  itemsList.value = [];

  for (const item of items.value) {
    // skip items that are replacements
    if (replacementItemsStore.getBaseItem(item.id) !== undefined) {
      continue;
    }

    // unique unit codes for this item, first item is the base unit
    const itemUnitCodes = new Map<string, string>();
    itemUnitCodes.set(item.baseUnitCode, getUnitCodeDisplayName(item, item.id, item.baseUnitCode));
    tradeItemsStore.getByItemId(item.id).forEach(ti => itemUnitCodes.set(ti.unitCode, getUnitCodeDisplayName(item, ti.itemId, ti.unitCode)));

    const supplierItem = supplierItemsStore.getByItemId(item.id);
    if (supplierItem) {
      const itemSupplier = supplierItem.supplier;
      if (itemSupplier === undefined) {
        itemsList.value.push(new SelectableItem(item, itemUnitCodes, '', 1, item.baseUnitCode)); // item is not assigned to any supplier
      }
      else {
        if (itemSupplier.id === props.supplierId) {
          itemsList.value.push(new SelectableItem(item, itemUnitCodes, supplierItem.code, supplierItem.proposedOrderQuantity ?? 1, supplierItem.orderUnitCode, supplierItem.name, supplierItem.description, itemSupplier)); // item is assigned to this supplier
        } else {
          itemsList.value.push(new SelectableItem(item, itemUnitCodes, '', supplierItem.proposedOrderQuantity ?? 1, item.baseUnitCode, supplierItem.name, supplierItem.description, itemSupplier)); // item is assigned to another supplier
        }
      }
    } else {
      itemsList.value.push(new SelectableItem(item, itemUnitCodes, '', 1, item.baseUnitCode)); // item is not assigned to any supplier
    }
  }
  itemsList.value.sort((a, b) => a.item?.name.localeCompare(b.item?.name ?? '') ?? 0);
}

const assignCurrentSupplier = async (entry: SelectableItem) => {
  if (currentSupplier.value === undefined) return;
  entry.code = entry.item.code;
  
  // Focus the input field after assigning
  await nextTick();
  const inputRef = document.getElementById(`codeInput${entry.item.id}`) as HTMLInputElement;
  if (inputRef) {
    inputRef.focus();
    inputRef.select();
  }
};
</script>

<style scoped type="scss">
input[type=number] {
  text-align: right;
}

th {
  font-weight: bold;
}

tr {
  border-bottom: 1px dashed #ccc;

  &:last-child {
    border-bottom: none;
  }
}

th,
td {
  padding-left: .5rem;
  padding-right: .5rem;
}

tr.is-filled>td {
  background-color: var(--mel-row-filled-bg);
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg);
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary-bg-subtle);
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success-bg-subtle);
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning-bg-subtle);
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger-bg-subtle);
}

.required::after {
  content: " *";
  color: var(--bs-danger);
}
</style>
